import React, { useState, useEffect } from "react";
import { FaBook, FaUser, FaChalkboardTeacher, FaCalendarAlt, FaClipboardList } from "react-icons/fa";
import { getRequest, postRequest, putRequest, deleteRequest } from "../../apiWrapper";
import AddStudentGoalModal from "./AddStudentGoalModal"; // モーダルフォームのインポート

const PreviousLessonReports = ({ studentid, selectedSubject, refresh }) => {
  const [loading, setLoading] = useState(true);
  const [reports, setReports] = useState([]);
  const [subjectColors, setSubjectColors] = useState({});
  const [allSubjects, setAllSubjects] = useState([]); // 全科目情報
  const [studentGoal, setStudentGoal] = useState(null); // AIで登録された課題
  const [isModalOpen, setIsModalOpen] = useState(false); // モーダルの開閉管理
  const [isCreatingAI, setIsCreatingAI] = useState(false); // ←追加


  // API から科目ごとのボーダーカラーを取得
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const data = await getRequest("settings/default");
        if (data && data.colors && data.colors.subjectBorders) {
          setSubjectColors(data.colors.subjectBorders);
        }
      } catch (error) {
        console.error("❌ 設定の取得エラー:", error);
      }
    };
    fetchSettings();
  }, []);

  // API から全科目情報を取得
  useEffect(() => {
    const fetchAllSubjects = async () => {
      try {
        const data = await getRequest("subjects");
        if (data && data.subjects) {
          setAllSubjects(data.subjects);
        }
      } catch (error) {
        console.error("❌ 科目情報の取得エラー:", error);
      }
    };
    fetchAllSubjects();
  }, []);

  // 選択された科目に対応する過去レポートを取得
  useEffect(() => {
    if (!studentid) return;
    if (!selectedSubject) {
      setReports([]);
      return;
    }
    const fetchReports = async () => {
      setLoading(true);
      try {
        // allSubjects から selectedSubject と同じカテゴリの科目名リストを取得
        const selectedSubjectObj = allSubjects.find((s) => s.name === selectedSubject);
        let subjectQuery;
        if (selectedSubjectObj) {
          subjectQuery = allSubjects
            .filter((s) => s.category === selectedSubjectObj.category)
            .map((s) => s.name);
        } else {
          subjectQuery = [selectedSubject];
        }
        const endpoint = `lessons/previous?studentid=${studentid}&subjects=${subjectQuery.join(",")}&limit=12`;
        const data = await getRequest(endpoint);
        if (data && data.reports) {
          setReports(data.reports);
        } else {
          setReports([]);
        }
      } catch (error) {
        console.error("❌ 過去のレポート取得エラー:", error);
        setReports([]);
      } finally {
        setLoading(false);
      }
    };

    fetchReports();
  }, [studentid, selectedSubject, refresh, allSubjects, studentGoal]);

  // AI提案ボタンのクリック時に呼び出す関数
  const handleAIClick = async (report) => {
    if (report.ai_suggested) return;

    try {
      setIsCreatingAI(true);
      const res = await postRequest('suggested_goals', {
        lessonid: report.id,
        studentid: studentid
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || "AI提案API失敗");
      }

      const data = await res.json();
      if (data && data.goal) {
        setStudentGoal(data.goal);
        setIsModalOpen(true);
      }
    } catch (error) {
      console.error("❌ AI提案取得エラー:", error);
    } finally {
      setIsCreatingAI(false);
    }
  };


  return (
    <div className="mt-6">
      <h3 className="text-lg font-bold mb-4">📖 過去の授業報告</h3>
      {loading ? (
        <p className="text-gray-500">読み込み中...</p>
      ) : reports.length === 0 ? (
        <p className="text-gray-500">過去の授業報告はありません。</p>
      ) : (
        reports.map((report) => (
          <div
            key={report.id}
            className="p-4 mb-4 rounded-lg shadow-md bg-gray-50"
            style={{ borderLeft: `8px solid ${subjectColors[report.subject] || "rgb(128,128,128)"}` }}
          >
            <div className="flex justify-between items-center mb-2">
              <h4 className="text-lg font-semibold">{report.subject}</h4>
              <span className="text-sm text-gray-600">{report.timestamp_jst.split("T")[0]}</span>
            </div>

            {/* AI提案ボタン */}
            <div className="flex justify-between items-center">
              {!report.ai_suggested ? (
                <button
                  className="bg-blue-500 text-white px-3 py-1 rounded-lg"
                  onClick={() => handleAIClick(report)}
                >
                  AI
                </button>
              ) : (
                <span className="text-green-500 font-semibold">AI提案済み</span>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <p className="flex items-center text-gray-700">
                <FaUser className="mr-2 text-blue-500" />
                <span className="font-bold">担当:</span> {report.teachername}
              </p>
              <p className="flex items-center text-gray-700">
                <FaCalendarAlt className="mr-2 text-green-500" />
                <span className="font-bold">宿題状況:</span> {report.homeworkstatus}
              </p>
            </div>
            <p className="flex items-center text-gray-700">
              <FaChalkboardTeacher className="mr-2 text-yellow-500" />
              <span className="font-bold">単元:</span> {report.unit}
            </p>
            <p className="flex items-center text-gray-700">
              <FaBook className="mr-2 text-purple-500" />
              <span className="font-bold">教材:</span> {report.material}
            </p>

            <div className="mt-2 p-2 bg-white rounded shadow-sm border">
              <p className="font-bold text-gray-600">授業考察</p>
              <p className="text-gray-700">{report.lessonnotes || "なし"}</p>
            </div>
            <div className="mt-2 p-2 bg-white rounded shadow-sm border">
              <p className="font-bold text-gray-600">宿題</p>
              <p className="text-gray-700">{report.homework || "なし"}</p>
            </div>
            <div className="mt-2 p-2 bg-white rounded shadow-sm border">
              <p className="font-bold text-gray-600">次回範囲</p>
              <p className="text-gray-700">{report.nextlesson || "なし"}</p>
            </div>

            <div className="mt-2 flex flex-wrap gap-2">
              <span className="px-3 py-1 text-sm font-semibold rounded-full bg-blue-100 text-blue-700">
                進捗: {report.schoolprogress}
              </span>
              <span className="px-3 py-1 text-sm font-semibold rounded-full bg-green-100 text-green-700">
                態度: {report.lessonattitude}
              </span>
              <span className="px-3 py-1 text-sm font-semibold rounded-full bg-red-100 text-red-700">
                理解度: {report.understanding}
              </span>
            </div>

            {report.teachermemo && (
              <div className="mt-2 p-2 bg-gray-100 rounded border-l-4 border-gray-400">
                <p className="font-bold text-gray-600">講師メモ</p>
                <p className="text-gray-700">{report.teachermemo}</p>
              </div>
            )}
          </div>
        ))
      )}
      {/* 提案モーダル */}
      {isModalOpen && (
        <AddStudentGoalModal
          onClose={() => setIsModalOpen(false)}
          onAdded={(goal) => {
            setIsModalOpen(false);
          }}
          studentid={studentid}
          goalData={studentGoal}
        />
      )}
      {isCreatingAI && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center">
          <div className="flex flex-col items-center gap-4 text-white">
            <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-white border-solid"></div>
            <p className="text-lg font-semibold">AIが課題を作成中です...</p>
          </div>
        </div>
      )}

    </div>
  );
};

export default PreviousLessonReports;
